/// <reference lib="dom" />
/**
 * webPortalDraw.web.ts — Canvas2D draw helpers for the end-of-level portal
 * archway and activation pedestal. Split out of webTileDraw.web.ts to keep
 * each file under the AGENTS.md 300-line limit.
 *
 * Web-only (.web.ts). Stateless reads — never mutates gameState.
 * @module webPortalDraw
 */

import { VIEWPORT_WIDTH, TILE_SIZE } from '../../constants/gameConstants';
import {
  portalX as getPortalX,
  PORTAL_DRAW_W as FINALE_PORTAL_DRAW_W,
  PEDESTAL_DRAW_SIZE as FINALE_PEDESTAL_DRAW_SIZE,
  GROUND_ROW,
  GATE_COL,
} from '../scenes/finaleState';

// ── Shared portal positioning constants ───────────────────────────────────
// DECISION: Portal layout derives from finaleState (single source of truth for
// GATE_COL which can change via setFinaleColumns). Rendering-only values kept local.
const PORTAL_DRAW_W      = FINALE_PORTAL_DRAW_W;
const PORTAL_DRAW_H      = 5 * TILE_SIZE;  // 80px tall (rendering-only)
const PEDESTAL_DRAW_SIZE = FINALE_PEDESTAL_DRAW_SIZE;
// Row 14 is the grass tile row at the portal's position (cols 400-434).
const PORTAL_GROUND_Y    = GROUND_ROW * TILE_SIZE;
// +4 pushes the portal base onto the grass surface visual line.
const PORTAL_Y_NUDGE     = 4;

// DECISION: Crop fractions for portal front/back split — eyeballed from the
// 64×64 sprite. Left column ≈28%, right starts ≈72%, arch top ≈25%.
const PORTAL_LEFT_FRAC  = 0.28;
const PORTAL_RIGHT_FRAC = 0.72;
const PORTAL_ARCH_FRAC  = 0.25;

/** Draw the end-of-level portal archway and activation pedestal using sprites. */
export function drawExitPortal(
  ctx: CanvasRenderingContext2D,
  camX: number,
  camY: number,
  portalActiveImg: HTMLImageElement | null,
  portalInactiveImg: HTMLImageElement | null,
  portalPedestalImg: HTMLImageElement | null,
  isActive: boolean,
): void {
  // DECISION: GATE_COL < 0 is the sentinel PlayScene sets for non-finale
  // levels so the portal/pedestal stay hidden. Without this guard the
  // module-scoped GATE_COL leaks across level loads (e.g. 1-3 → 1-4).
  if (GATE_COL < 0) return;
  const portalWorldX = getPortalX();
  const portalX = portalWorldX - camX;
  const portalVisible = portalX <= VIEWPORT_WIDTH && portalX + PORTAL_DRAW_W >= 0;
  if (!portalVisible) return;

  const portalY = PORTAL_GROUND_Y - PORTAL_DRAW_H - camY + PORTAL_Y_NUDGE;

  // DECISION: Draw only the back-layer portions of the portal — left pillar
  // and interior below the arch. The front layer (drawExitPortalFront) draws
  // the right pillar + top arch. Drawing the full sprite here would double
  // semi-transparent pixels (glow/AA), causing darkening artifacts.
  const portalImg = isActive ? portalActiveImg : portalInactiveImg;
  if (portalImg && portalImg.naturalWidth > 0) {
    const srcW = portalImg.naturalWidth;
    const srcH = portalImg.naturalHeight;
    const scaleX = PORTAL_DRAW_W / srcW;
    const scaleY = PORTAL_DRAW_H / srcH;
    const leftW  = Math.round(srcW * PORTAL_LEFT_FRAC);
    const rightX = Math.round(srcW * PORTAL_RIGHT_FRAC);
    const archH  = Math.round(srcH * PORTAL_ARCH_FRAC);

    // Left pillar (full height)
    ctx.drawImage(portalImg,
      0, 0, leftW, srcH,
      portalX, portalY, leftW * scaleX, PORTAL_DRAW_H);
    // Interior below arch (middle span)
    ctx.drawImage(portalImg,
      leftW, archH, rightX - leftW, srcH - archH,
      portalX + leftW * scaleX, portalY + archH * scaleY,
      (rightX - leftW) * scaleX, (srcH - archH) * scaleY);
  } else {
    // Fallback: simple colored rectangle if sprite not loaded
    ctx.fillStyle = isActive ? '#4a1a8a' : '#2a2a32';
    ctx.fillRect(portalX, portalY, PORTAL_DRAW_W, PORTAL_DRAW_H);
  }

  // Draw the activation pedestal 4 tiles to the left of the portal
  const pedestalWorldX = portalWorldX - 4 * TILE_SIZE;
  const pedestalX = pedestalWorldX - camX;
  // DECISION: +1 slight nudge so pedestal base rests on the grass surface
  // without clipping into it. The sprite has ~1px transparent bottom padding.
  const pedestalY = PORTAL_GROUND_Y - PEDESTAL_DRAW_SIZE - camY + 1;
  const pedestalVisible = pedestalX <= VIEWPORT_WIDTH && pedestalX + PEDESTAL_DRAW_SIZE >= 0;
  if (pedestalVisible && portalPedestalImg && portalPedestalImg.naturalWidth > 0) {
    ctx.drawImage(portalPedestalImg, pedestalX, pedestalY, PEDESTAL_DRAW_SIZE, PEDESTAL_DRAW_SIZE);
  }
}

/** Draw the portal frame (right pillar + arch) as a front layer on top of Nova. */
export function drawExitPortalFront(
  ctx: CanvasRenderingContext2D,
  camX: number,
  camY: number,
  portalActiveImg: HTMLImageElement | null,
  portalInactiveImg: HTMLImageElement | null,
  isActive: boolean,
): void {
  // DECISION: Mirror the GATE_COL sentinel guard from drawExitPortal so the
  // front layer also skips rendering on non-finale levels.
  if (GATE_COL < 0) return;
  const portalWorldX = getPortalX();
  const portalScreenX = portalWorldX - camX;
  if (portalScreenX > VIEWPORT_WIDTH || portalScreenX + PORTAL_DRAW_W < 0) return;

  const portalScreenY = PORTAL_GROUND_Y - PORTAL_DRAW_H - camY + PORTAL_Y_NUDGE;
  const portalImg = isActive ? portalActiveImg : portalInactiveImg;
  if (!portalImg || portalImg.naturalWidth <= 0) return;

  const srcW = portalImg.naturalWidth;
  const srcH = portalImg.naturalHeight;
  const scaleX = PORTAL_DRAW_W / srcW;
  const scaleY = PORTAL_DRAW_H / srcH;

  const rightX = Math.round(srcW * PORTAL_RIGHT_FRAC);
  const rightW = srcW - rightX;
  const leftW  = Math.round(srcW * PORTAL_LEFT_FRAC);
  const archH  = Math.round(srcH * PORTAL_ARCH_FRAC);

  // DECISION: Only the right pillar is drawn in the front layer. The left
  // pillar stays in the back layer only, so Nova walks IN FRONT of the
  // near/left arch, through the gap, and BEHIND the far/right arch.

  // Right pillar (full height)
  ctx.drawImage(portalImg,
    rightX, 0, rightW, srcH,
    portalScreenX + rightX * scaleX, portalScreenY, rightW * scaleX, PORTAL_DRAW_H);

  // Top arch (middle span between pillars)
  ctx.drawImage(portalImg,
    leftW, 0, rightX - leftW, archH,
    portalScreenX + leftW * scaleX, portalScreenY,
    (rightX - leftW) * scaleX, archH * scaleY);
}
